import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { API_BASE } from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Download, TrendingUp, TrendingDown, Scale, BookOpen, Landmark } from "lucide-react";
import { toast } from "sonner";

export default function AccountsBI() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [stmtDate, setStmtDate] = useState(new Date().toISOString().slice(0, 10));
  const [stmtBal, setStmtBal] = useState(0);
  const [recon, setRecon] = useState(null);

  const { data: pl } = useQuery({ queryKey: ["pl", from, to], queryFn: () => api.get(`/accounts-bi/profit-loss?from_date=${from}&to_date=${to}`).then((r) => r.data) });
  const { data: bs } = useQuery({ queryKey: ["bs"], queryFn: () => api.get("/accounts-bi/balance-sheet").then((r) => r.data) });
  const { data: tb } = useQuery({ queryKey: ["tb"], queryFn: () => api.get("/accounts-bi/trial-balance").then((r) => r.data) });
  const { data: cb } = useQuery({ queryKey: ["cb", from, to], queryFn: () => api.get(`/accounts-bi/cash-book?from_date=${from}&to_date=${to}`).then((r) => r.data) });

  const download = async (kind) => {
    // Cookie auth is sent automatically via credentials: 'include'
    const res = await fetch(`${API_BASE}/exports/report/${kind}.xlsx`, { credentials: "include" });
    if (!res.ok) { toast.error("Export failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${kind}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  };

  const runRecon = async () => {
    try {
      const { data } = await api.post("/accounts-bi/bank-reconciliation", { statement_date: stmtDate, statement_balance: Number(stmtBal) });
      setRecon(data);
    } catch (e) { toast.error("Reconciliation failed"); }
  };

  return (
    <div className="space-y-4" data-testid="accounts-bi-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Financial Statements</h1>
          <p className="text-sm text-muted-foreground">P&L, Balance Sheet, Trial Balance, Cash Book, Bank Reconciliation.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => download("invoices")} data-testid="btn-export-inv"><Download size={12} className="mr-1" /> Invoices</Button>
          <Button variant="outline" size="sm" onClick={() => download("outstanding")} data-testid="btn-export-out"><Download size={12} className="mr-1" /> Outstanding</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div><Label>From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" /></div>
        <div><Label>To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" /></div>
      </div>

      <Tabs defaultValue="pl">
        <TabsList>
          <TabsTrigger value="pl" data-testid="tab-pl">P&L</TabsTrigger>
          <TabsTrigger value="bs" data-testid="tab-bs">Balance Sheet</TabsTrigger>
          <TabsTrigger value="tb" data-testid="tab-tb">Trial Balance</TabsTrigger>
          <TabsTrigger value="cb" data-testid="tab-cb">Cash Book</TabsTrigger>
          <TabsTrigger value="recon" data-testid="tab-recon">Bank Reconciliation</TabsTrigger>
        </TabsList>

        <TabsContent value="pl" className="mt-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <KPI icon={TrendingUp} label="Revenue" value={`₹${(pl?.revenue || 0).toLocaleString("en-IN")}`} />
            <KPI icon={TrendingDown} label="COGS" value={`₹${(pl?.cost_of_goods_sold || 0).toLocaleString("en-IN")}`} />
            <KPI icon={Scale} label="Gross Profit" value={`₹${(pl?.gross_profit || 0).toLocaleString("en-IN")}`} highlight />
            <KPI icon={Scale} label="Operating Profit" value={`₹${(pl?.operating_profit || 0).toLocaleString("en-IN")}`} highlight />
          </div>
          <Card className="mt-3"><CardHeader><CardTitle>Expense breakdown</CardTitle></CardHeader><CardContent className="p-0">
            <table className="data-table w-full">
              <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground"><tr><th className="px-4 py-2">Category</th><th className="px-4 py-2 text-right">Amount</th></tr></thead>
              <tbody>
                {(pl?.expense_breakdown || []).map((e) => (
                  <tr key={e.category} className="border-b border-border/40 text-sm">
                    <td className="px-4 py-2 capitalize">{e.category}</td>
                    <td className="px-4 py-2 text-right font-mono">₹{e.amount.toLocaleString("en-IN")}</td>
                  </tr>
                ))}
                {(!pl?.expense_breakdown || pl.expense_breakdown.length === 0) && <tr><td colSpan={2} className="px-4 py-4 text-center text-sm text-muted-foreground">No expenses recorded.</td></tr>}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="bs" className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          <Card><CardHeader><CardTitle>Assets</CardTitle></CardHeader><CardContent className="space-y-2 text-sm">
            <Row label="Cash & Bank" v={bs?.assets?.cash_and_bank} />
            <Row label="Accounts Receivable" v={bs?.assets?.accounts_receivable} />
            <Row label="Inventory (Diesel)" v={bs?.assets?.inventory_diesel} />
            <Row label="Total Assets" v={bs?.assets?.total} bold />
          </CardContent></Card>
          <Card><CardHeader><CardTitle>Liabilities</CardTitle></CardHeader><CardContent className="space-y-2 text-sm">
            <Row label="Accounts Payable" v={bs?.liabilities?.accounts_payable} />
            <Row label="Total Liabilities" v={bs?.liabilities?.total} bold />
          </CardContent></Card>
          <Card><CardHeader><CardTitle>Equity</CardTitle></CardHeader><CardContent className="space-y-2 text-sm">
            <Row label="Owner's Equity" v={bs?.equity} bold />
            <Row label="Balance Check" v={bs?.check} />
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="tb" className="mt-4">
          <Card><CardContent className="p-0">
            <table className="data-table w-full">
              <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground"><tr><th className="px-4 py-2">Account</th><th className="px-4 py-2 text-right">Debit</th><th className="px-4 py-2 text-right">Credit</th></tr></thead>
              <tbody>
                {(tb?.rows || []).map((r) => (
                  <tr key={r.account} className="border-b border-border/40 text-sm">
                    <td className="px-4 py-2 font-semibold">{r.account}</td>
                    <td className="px-4 py-2 text-right font-mono">{r.debit ? `₹${r.debit.toLocaleString("en-IN")}` : "—"}</td>
                    <td className="px-4 py-2 text-right font-mono">{r.credit ? `₹${r.credit.toLocaleString("en-IN")}` : "—"}</td>
                  </tr>
                ))}
                <tr className="border-b border-border/60 bg-muted/40 text-sm font-bold">
                  <td className="px-4 py-2">TOTAL</td>
                  <td className="px-4 py-2 text-right font-mono">₹{(tb?.total_debit || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-2 text-right font-mono">₹{(tb?.total_credit || 0).toLocaleString("en-IN")}</td>
                </tr>
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="cb" className="mt-4">
          <Card><CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"><CardTitle>Cash Book</CardTitle><div className="text-sm">Closing: <b className="font-mono text-primary">₹{(cb?.closing_balance || 0).toLocaleString("en-IN")}</b></div></CardHeader><CardContent className="p-0">
            <table className="data-table w-full">
              <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground"><tr><th className="px-4 py-2">Date</th><th className="px-4 py-2">Type</th><th className="px-4 py-2">Ref</th><th className="px-4 py-2">Particulars</th><th className="px-4 py-2 text-right">Debit</th><th className="px-4 py-2 text-right">Credit</th><th className="px-4 py-2 text-right">Balance</th></tr></thead>
              <tbody>
                {(cb?.items || []).slice(-40).reverse().map((e) => (
                  <tr key={`${e.code}-${e.date}`} className="border-b border-border/40 text-sm">
                    <td className="px-4 py-2 text-xs">{e.date.slice(0, 10)}</td>
                    <td className="px-4 py-2 text-xs uppercase">{e.type}</td>
                    <td className="px-4 py-2 font-mono text-xs">{e.code}</td>
                    <td className="px-4 py-2 text-xs">{e.particulars}</td>
                    <td className="px-4 py-2 text-right font-mono">{e.debit ? `₹${e.debit.toLocaleString("en-IN")}` : "—"}</td>
                    <td className="px-4 py-2 text-right font-mono">{e.credit ? `₹${e.credit.toLocaleString("en-IN")}` : "—"}</td>
                    <td className="px-4 py-2 text-right font-mono font-bold">₹{e.balance.toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="recon" className="mt-4 space-y-3">
          <Card><CardHeader><CardTitle>Bank Reconciliation</CardTitle></CardHeader><CardContent>
            <div className="flex flex-wrap items-end gap-3">
              <div><Label>Statement date</Label><Input type="date" value={stmtDate} onChange={(e) => setStmtDate(e.target.value)} className="w-40" /></div>
              <div><Label>Statement balance ₹</Label><Input type="number" value={stmtBal} onChange={(e) => setStmtBal(e.target.value)} className="w-40" data-testid="recon-bal" /></div>
              <Button onClick={runRecon} data-testid="btn-run-recon"><Landmark size={14} className="mr-1" /> Reconcile</Button>
            </div>
            {recon && (
              <div className="mt-4 rounded-lg border border-border/60 p-4">
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div><div className="text-xs uppercase text-muted-foreground">Book balance</div><div className="font-mono text-lg font-bold">₹{recon.book_balance.toLocaleString("en-IN")}</div></div>
                  <div><div className="text-xs uppercase text-muted-foreground">Statement balance</div><div className="font-mono text-lg font-bold">₹{recon.statement_balance.toLocaleString("en-IN")}</div></div>
                  <div><div className="text-xs uppercase text-muted-foreground">Difference</div><div className={`font-mono text-lg font-bold ${recon.reconciled ? "text-status-delivered" : "text-status-cancelled"}`}>₹{recon.difference.toLocaleString("en-IN")}</div></div>
                </div>
                <div className={`mt-3 rounded p-2 text-center text-sm font-semibold ${recon.reconciled ? "bg-status-delivered/10 text-status-delivered" : "bg-status-cancelled/10 text-status-cancelled"}`}>
                  {recon.reconciled ? "✓ Reconciled" : "⚠ Difference detected — investigate"}
                </div>
              </div>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const KPI = ({ icon: Icon, label, value, highlight }) => (
  <div className="kpi-card">
    <div className="flex items-center justify-between text-muted-foreground"><div className="text-xs uppercase tracking-widest">{label}</div><Icon size={16} /></div>
    <div className={`mt-2 font-display text-2xl font-black ${highlight ? "text-primary" : ""}`}>{value}</div>
  </div>
);
const Row = ({ label, v, bold }) => (
  <div className={`flex items-center justify-between border-b border-border/40 py-1 last:border-0 ${bold ? "font-bold" : ""}`}>
    <span className={bold ? "" : "text-muted-foreground"}>{label}</span>
    <span className="font-mono">₹{(v || 0).toLocaleString("en-IN")}</span>
  </div>
);
